// Pure, zero-dependency grid placement engine for the Home canvas: explicit
// per-widget (x,y) cell coordinates with Grafana-style push-aside collision and
// upward gravity. Operates on plain Box objects (never React / DOM), so every
// function is total, immutable (returns new arrays), and unit-testable. `cols`
// is the column count (12 for the Home grid). All coords are grid CELLS, 0-based.
export type Box = { id: string; x: number; y: number; w: number; h: number };

const MAX_H = 100; // runaway guard for h; the app's real max (16) is enforced upstream

// Half-open rectangles: a occupies [x, x+w) x [y, y+h). Edge-adjacent tiles do
// not overlap. Boxes are assumed clamped (finite); NaN coords never overlap.
export function overlaps(a: Box, b: Box): boolean {
  return a.x < b.x + b.w && b.x < a.x + a.w && a.y < b.y + b.h && b.y < a.y + a.h;
}

// The single sanitization point: coerce non-finite inputs to safe defaults, clamp
// w to [1,cols] and h to [1,MAX_H], keep the box off the right edge, floor y at 0.
// Genuinely total — every downstream fn can trust its output.
export function clampBox(b: Box, cols: number): Box {
  const w = Math.max(1, Math.min(Math.round(Number.isFinite(b.w) ? b.w : 1), cols));
  const h = Math.max(1, Math.min(Math.round(Number.isFinite(b.h) ? b.h : 1), MAX_H));
  let x = Math.round(Number.isFinite(b.x) ? b.x : 0);
  if (x < 0) x = 0;
  if (x + w > cols) x = cols - w;
  const y = Math.max(0, Math.round(Number.isFinite(b.y) ? b.y : 0));
  return { ...b, x, y, w, h };
}

// Deterministic stack order: top-to-bottom, then left-to-right, then id.
function readingOrder(a: Box, b: Box): number {
  return (a.y - b.y) || (a.x - b.x) || (a.id < b.id ? -1 : a.id > b.id ? 1 : 0);
}

// BFS push-DOWN. `movedId` is the anchor: authoritative, never displaced.
// ASSUMES a non-overlapping input layout; it resolves only the moved box's
// cascade (anything overlapping a mover is shoved straight below it; pushed boxes
// become movers). Pure-down => cannot cycle; the guard is belt-and-suspenders.
// Unrelated pre-existing overlaps are NOT repaired here (moveElement/resolveResize
// only ever feed it clean layouts). Returns boxes in the ORIGINAL input order.
export function resolveCollisions(boxes: Box[], movedId: string, cols: number): Box[] {
  const map = new Map<string, Box>();
  for (const b of boxes) map.set(b.id, clampBox(b, cols));
  const queue: string[] = [movedId];
  let guard = boxes.length * boxes.length + 8;
  while (queue.length && guard-- > 0) {
    const mover = map.get(queue.shift() as string);
    if (!mover) continue;
    const hit = [...map.values()]
      .filter((o) => o.id !== mover.id && o.id !== movedId && overlaps(mover, o))
      .sort(readingOrder);
    for (const o of hit) {
      const newY = mover.y + mover.h; // just below the mover
      if (newY > o.y) { map.set(o.id, { ...o, y: newY }); queue.push(o.id); }
    }
  }
  return boxes.map((b) => map.get(b.id) as Box);
}

// Gravity-up. `pinnedId` (if any) keeps AT LEAST its current row so a just-dropped
// tile stays put instead of flying to the top; everything else rises as far as it
// can without overlapping a settled box. Order-preserving. pinnedId=null => full
// gravity compaction. Note (intentional): a non-pinned tile that started BELOW the
// pinned one can rise ABOVE it into free space (Grafana leapfrog) — no overlap, no
// gap; it is a compaction consequence, not a bug.
export function compactPinned(boxes: Box[], pinnedId: string | null, cols: number): Box[] {
  const clamped = boxes.map((b) => clampBox(b, cols));
  const pinnedY = pinnedId ? (clamped.find((b) => b.id === pinnedId)?.y ?? 0) : 0;
  const placed: Box[] = [];
  const order = [...clamped].sort(readingOrder);
  for (const b of order) {
    let y = b.id === pinnedId ? pinnedY : 0; // pinned floor
    for (;;) {
      const cand = { ...b, y };
      const hit = placed.find((p) => overlaps(cand, p));
      if (!hit) break;
      y = hit.y + hit.h; // jump just under the blocker, retry
    }
    placed.push({ ...b, y });
  }
  const byId = new Map(placed.map((p) => [p.id, p]));
  return boxes.map((b) => byId.get(b.id) as Box);
}

// Drop entry point: set the dragged box to the requested cell (clamped), push
// colliders down (anchor = dragged), then gravity with the dragged box pinned.
export function moveElement(boxes: Box[], id: string, toX: number, toY: number, cols: number): Box[] {
  if (!boxes.some((b) => b.id === id)) return boxes;
  const moved = boxes.map((b) => (b.id === id ? clampBox({ ...b, x: toX, y: toY }, cols) : b));
  const pushed = resolveCollisions(moved, id, cols);
  return compactPinned(pushed, id, cols);
}

// Resize entry point: change w/h in place (clamped), push colliders down, gravity
// with the resized box pinned. Growing pushes overlapped neighbors down; shrinking
// frees space and gravity pulls tiles up.
export function resolveResize(boxes: Box[], id: string, newW: number, newH: number, cols: number): Box[] {
  const grown = boxes.map((b) => (b.id === id ? clampBox({ ...b, w: newW, h: newH }, cols) : b));
  const pushed = resolveCollisions(grown, id, cols);
  return compactPinned(pushed, id, cols);
}

// Corner-resize entry: set the box's FULL geometry (x/y/w/h, clamped) so a resize
// can move the tile's origin (dragging any corner), not just grow from the top-
// left. Pushes overlapped neighbors down (anchor = resized box), then gravity
// with it pinned — same semantics as moveElement/resolveResize.
export function resolveResizeBox(boxes: Box[], id: string, box: { x: number; y: number; w: number; h: number }, cols: number): Box[] {
  const set = boxes.map((b) => (b.id === id ? clampBox({ ...b, x: box.x, y: box.y, w: box.w, h: box.h }, cols) : b));
  const pushed = resolveCollisions(set, id, cols);
  return compactPinned(pushed, id, cols);
}

// Backward-compat + repair: fill x/y for boxes that lack a finite position, AND
// re-fit any EXPLICIT positions that overlap already-placed tiles (footprint
// changes / backend x-clamp can shove stored coords onto a neighbor). Seeds
// non-overlapping explicit boxes, defers overlapping ones, then first-fits the
// unpositioned + deferred in array order (scan y=0.., x=0..cols-w) — reproducing
// grid-auto-flow:row dense incl. gap-backfill. Order-preserving; result is always
// overlap-free.
export function derivePositions(boxesInOrder: Box[], cols: number): Box[] {
  const isPos = (b: Box) => Number.isFinite(b.x) && Number.isFinite(b.y);
  const placed: Box[] = [];
  const deferred = new Set<string>();
  for (const b of boxesInOrder) {
    if (!isPos(b)) continue;
    const cb = clampBox(b, cols);
    if (placed.some((p) => overlaps(cb, p))) deferred.add(b.id); // stored coords overlap -> re-fit
    else placed.push(cb);
  }
  for (const b0 of boxesInOrder) {
    if (isPos(b0) && !deferred.has(b0.id)) continue;
    const w = Math.max(1, Math.min(Math.round(Number.isFinite(b0.w) ? b0.w : 1), cols));
    const h = Math.max(1, Math.min(Math.round(Number.isFinite(b0.h) ? b0.h : 1), MAX_H));
    let done = false;
    for (let y = 0; !done; y++) {
      for (let x = 0; x + w <= cols; x++) {
        const cand = { id: b0.id, x, y, w, h };
        if (!placed.some((p) => overlaps(cand, p))) { placed.push(cand); done = true; break; }
      }
    }
  }
  const byId = new Map(placed.map((p) => [p.id, p]));
  return boxesInOrder.map((b) => byId.get(b.id) ?? clampBox(b, cols));
}

// Bottom-most occupied row (max y+h). 0 for an empty list.
export function bottomRow(boxes: Box[]): number {
  return boxes.reduce((m, b) => Math.max(m, b.y + b.h), 0);
}
